import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import { Image } from 'expo-image'
import Typography from '@ui/typography'
import Status from '@ui/elements/Status'
import ImageNavigation from '@ui/components/ImageNavigation'
import { StepOption } from '@order/steps/types'
import { getFirstExistingKey } from 'utils/i18nUtils'
import {
  gap as itemGap,
  width as itemWidth,
} from '@ui/elements/Attribute/styles'
import { useOptionAttributesTiers } from './useOptionAttributes'
import { Active } from './types'
import Attribute from '@ui/elements/Attribute'
import { colorOption } from '@order/Package/styles'
import { optionTextColor } from '@order/PricePackage/styles'
import { hex } from '@ui/utils/hex'
import FormattedText from '@ui/typography/FormattedText'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'

interface Props {
  active: Active
  setActive: (active: Active) => void
  options: StepOption[]
}

export default function ModalBody({ active, setActive, options }: Props) {
  const { t, i18n } = useTranslation(['attributes'])

  const option = options.find((option) => option.id === active.optionId)
  const attribute = option?.attributes?.[active.activeIndex]

  const tiers = useOptionAttributesTiers(options)
  const attributeInTiers = attribute ? tiers[attribute.name] : []

  if (!option || !attribute) {
    return null
  }

  const optionAttributes = option.attributes || []
  const key = `attributes:${attribute.name}.info`

  const body = getFirstExistingKey(i18n, [`${key}.body`])

  return (
    <Animated.View className="flex w-full flex-col pb-8">
      <View className="p-8">
        <View className="flex w-full flex-col gap-8 rounded-2xl border border-black/10 p-8">
          <View className="flex w-full flex-col items-center justify-start gap-4">
            {attributeInTiers && attributeInTiers.length > 0 && (
              <View className="flex flex-row items-center justify-center gap-4 pb-2">
                {attributeInTiers.map((tier) => {
                  const color = optionTextColor[colorOption[tier] || 'default']
                  return (
                    <Status
                      key={tier}
                      text={tier}
                      colors={{ front: color, back: hex(color, 0.1) }}
                    />
                  )
                })}
              </View>
            )}
            <Typography variant="LGB" className="color-text" numberOfLines={1}>
              {t(`${key}.title`).replaceAll('\n', ' ')}
            </Typography>
            <FormattedText
              text={t(`${key}.desc`)}
              variant="MD"
              className="color-desc px-8 text-center"
              classNameBold="color-text font-text-bold"
              style={{ minHeight: 48 }}
              // numberOfLines={2}
            />
          </View>
          {attribute.image ? (
            <View className="aspect-video w-full">
              <Image
                source={attribute.image.url}
                contentFit="cover"
                cachePolicy="memory-disk"
                transition={150}
                style={{
                  width: '100%',
                  height: '100%',
                  aspectRatio: 16 / 9,
                  borderRadius: 32,
                }}
              />
            </View>
          ) : (
            <View
              className="realtive inset w-full rounded-full bg-black/10"
              style={{ height: 1 }}
            >
              <Gradient
                colors={[
                  colors.white,
                  colors.white0,
                  colors.black10,
                  colors.black10,
                  colors.white0,
                  colors.white,
                ]}
                start={[0, 0.5]}
                end={[1, 0.5]}
              />
            </View>
          )}
          {body && (
            <View>
              <FormattedText
                variant="SM"
                text={t(body)}
                className="color-desc"
                classNameBold="color-text/75 font-text-semibold"
              />
            </View>
          )}
        </View>
      </View>
      <ImageNavigation
        currentIndex={active.activeIndex}
        itemGap={itemGap}
        itemWidth={itemWidth}
      >
        {optionAttributes?.map((attribute, index) => (
          <Attribute
            key={attribute.name}
            text={t(`attributes:${attribute.name}.label`)}
            image={attribute.icon?.url}
            onPress={() => setActive({ ...active, activeIndex: index })}
            isActive={active.activeIndex === index}
            hideBorder={false}
          />
        ))}
      </ImageNavigation>
    </Animated.View>
  )
}
